<template>
    <div v-if="canBeRendered" class="video-modal modal fade" :id="'videoPlayerModal' + media.id" tabindex="-1" :aria-labelledby="'videoPlayerModal' + media.id + 'Label'" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-body video-header" :style="media.is_muted ? '&#45;&#45;volume-range: none;  &#45;&#45;mute-button: none;' : ''">
                    <div style="aspect-ratio: 16/9; object-fit: cover;background-color: black;display: flex;justify-content: center">
                        <video
                            :src="media.video_url"
                            style="max-height: 100%;"
                            controls
                            controlsList="nodownload"
                            oncontextmenu="return false;"
                        ></video>
                    </div>

                    <button class="btn btn-link" data-bs-dismiss="modal" aria-label="Close">
                        <Close></Close>
                    </button>
                </div>
                <div class="modal-body video-content">
                    <div class="container">
                        <h2 class="modal-title fs-3" :id="'videoPlayerModal' + media.id">{{ media.name }}</h2>
                        <div class="text-primary mb-1">
                            <strong>{{ $t('site.labels.date_added') }}:</strong> {{ DateTime.fromISO(media.created_at).toFormat('dd-LLL-yyyy') }}<br />
                            <template v-if="media?.contract?.creator">
                                <strong>{{ $t('site.labels.creator') }}:</strong> {{ media.contract.creator.alias }}
                            </template>
                        </div>

                        <div class="d-flex flex-wrap mt-3">
                            <a v-for="tag of media.tags" :key="tag.id" class="tag" :href="'/library?filter[tags]=' + tag.id">
                                {{ tag.name }}
                            </a>
                        </div>
                    </div>

                    <div class="d-flex align-items-start align-items-md-center justify-content-end flex-column flex-md-row buttons">
                        <div v-if="loggedIn">
                            <button v-if="!isFavorite" @click="favoriteMedia()" type="button" class="btn btn-primary btn-fill mb-3 mb-md-0" :disabled="isWorking">
                                <div class="d-flex align-items-center">
                                    {{ $t('site.actions.add_to_favorites') }}
                                    <span class="ms-2">
                                        <Heart></Heart>
                                    </span>
                                </div>
                            </button>
                            <button v-else @click="deleteFavoriteMedia()" type="button" class="btn btn-primary btn-fill mb-3 mb-md-0" :disabled="isWorking">
                                <div class="d-flex align-items-center">
                                    {{ $t('site.actions.remove_from_favorites') }}
                                    <span class="ms-2">
                                        <FullHeart></FullHeart>
                                    </span>
                                </div>
                            </button>
                            <button v-if="canDownload" type="button" :id="'download-' + media.id" :data-name="media.file_name" :data-media="media.id" data-initialized="false" class="btn btn-primary btn-fill ms-3 mb-3 mb-md-0" :disabled="isWorking">
                                <div class="d-flex align-items-center">
                                    {{ $t('site.actions.download') }}
                                    <span class="ms-2">
                                        <Download></Download>
                                    </span>
                                </div>
                            </button>
                        </div>

                        <a :href="'/library/' + media.id" class="btn btn-primary btn-fill ms-md-3">{{ $t('site.actions.show_details') }}</a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import Close from "@/site/components/Icons/Close.vue";
import Download from "@/site/components/Icons/Download.vue";
import {nextTick, onMounted, ref, watch} from "vue";
import {MediaItem} from "@/site/components/VideoElement.vue";
import Swal from "sweetalert2";
import {useI18n} from "vue-i18n";
import Heart from "@/site/components/Icons/Heart.vue";
import FullHeart from "@/site/components/Icons/FullHeart.vue";
import {isNil} from "lodash";
import {DateTime} from "luxon";

const { t } = useI18n();

const props = defineProps<{
    media: MediaItem;
}>();

const isFavorite = ref<boolean>(props.media.is_favorite);
const isWorking = ref<boolean>(false);
const canBeRendered = ref<boolean>(false);
const canDownload = window.global.canDownload;

const favoriteMedia = () => {
    isWorking.value = true;

    window.axios.post('favorite/' + props.media.id)
        .then(() => {
            isFavorite.value = true;
        })
        .finally(() => isWorking.value = false);
}

const deleteFavoriteMedia = () => {
    isWorking.value = true;

    window.axios.delete('favorite/' + props.media.id)
        .then(() => {
            isFavorite.value = false;
        })
        .finally(() => isWorking.value = false);
}

const loggedIn = window.global.loggedIn;

watch(() => props.media.is_favorite, newIsFavorite => {
    isFavorite.value = newIsFavorite;
});

onMounted(() => {
    const modalAlreadyExists = document.querySelectorAll('videoPlayerModal' + props.media.id).length;

    if (modalAlreadyExists) {
        return;
    }

    canBeRendered.value = true;
    nextTick(() => {
        const modal = document.getElementById('videoPlayerModal' + props.media.id) as HTMLElement;
        window.initializePlayerModal(modal);

        window.setupDownloadMedia(document.getElementById('download-' + props.media.id));
    });
});
</script>

<style scoped>

</style>
